import type { Collaboration, Configuration } from '@/api/types';
import { ArcMeter } from '@/components/ArcMeter';
import { BarList } from '@/components/BarList';
import { StatTile } from '@/components/StatTile';
import { TrendChart, formatCompact } from '@/components/TrendChart';

/**
 * Is this account a team, or five people who happen to share a login page?
 *
 * Five people sharing records is structurally hard to leave. Five people each working alone is five
 * trials, and it churns like one — and on an activity chart the two are indistinguishable, which is
 * why this panel exists at all.
 */
export function TeamPanel({ collaboration }: { collaboration: Collaboration }) {
  // Not collected is a completely different statement from "shares nothing", and rendering zeros
  // here would turn a missing database into a finding about the customer.
  if (!collaboration.collected) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>Team</h2>
        </div>
        <div className="callout callout-warning">
          <strong>No sharing data for this account.</strong>{' '}
          Collaboration is unknown, not zero — the reporting database for this tenant has not been
          read. The people list is unaffected.
        </div>
      </div>
    );
  }

  const ratio = collaboration.collaboration_ratio;

  const split = [
    { label: 'Shared', value: collaboration.shared_objects ?? 0 },
    {
      label: 'Personal',
      value: Math.max(0, (collaboration.total_objects ?? 0) - (collaboration.shared_objects ?? 0)),
    },
  ];

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Team</h2>
        <span className="t-label subtle">
          {collaboration.total_agents} {collaboration.total_agents === 1 ? 'person' : 'people'}
        </span>
      </div>

      <div style={{ display: 'grid', gap: 24, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        <div>
          <h3 className="t-label muted" style={{ margin: '0 0 8px' }}>Collaboration ratio</h3>
          {/* Null renders an empty track and an em dash. A tenant with nothing to share has no
              ratio; 0% would claim they have things to share and refuse to. */}
          <ArcMeter value={ratio} label="of objects shared" />
          <p className="t-caption subtle" style={{ margin: '8px 0 0' }}>
            {ratio === null
              ? 'No objects to share yet, so there is no ratio to compute.'
              : `${collaboration.shared_objects} of ${collaboration.total_objects} objects have been shared with someone.`}
          </p>
        </div>

        <div>
          <h3 className="t-label muted" style={{ margin: '0 0 8px' }}>Shared vs personal</h3>
          {/* Rows only when there is something to split. BarList floors every bar at 2% so it stays
              visible, which means a 0/0 split draws two little stubs — and two bars on screen say
              "there are objects here" to anyone glancing at it. An empty state says the truth.

              One hue for the same reason as everywhere else: both rows are the same measure
              (objects) sliced by whether they were shared, so a per-row colour would imply an
              identity that is not there. */}
          <BarList
            rows={(collaboration.total_objects ?? 0) > 0 ? split : []}
            limit={2}
            emptyLabel="No objects to share yet."
          />
        </div>
      </div>

      <div className="kpi-row" style={{ marginTop: 16 }}>
        <StatTile label="Active shares" value={collaboration.active_shares} />
        <StatTile
          label="Working alone"
          value={collaboration.isolated.length}
          tone={collaboration.isolated.length > 0 ? 'warning' : 'default'}
          sub={collaboration.isolated.length > 0 ? 'inside a shared account' : 'everyone collaborates'}
        />
        <StatTile
          label="Orphaned shares"
          value={collaboration.orphaned_shares}
          tone={(collaboration.orphaned_shares ?? 0) > 0 ? 'warning' : 'default'}
          sub="granted to someone who has left"
        />
      </div>

      {collaboration.isolated.length > 0 && (
        <>
          <h3 className="t-label muted" style={{ margin: '16px 0 8px' }}>
            Shares nothing, receives nothing
          </h3>
          <table className="table">
            <thead>
              <tr>
                <th>Person</th>
                <th className="num">Shared out</th>
                <th className="num">Shared with</th>
              </tr>
            </thead>
            <tbody>
              {collaboration.isolated.map((a) => (
                <tr key={a.user_id}>
                  <td>
                    {a.display_name || <span className="subtle">(no name)</span>}
                    <div className="t-caption muted">{a.email || '—'}</div>
                  </td>
                  <td className="num">{a.shared_out ?? '—'}</td>
                  <td className="num">{a.shared_with ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="t-caption subtle" style={{ margin: '8px 0 0' }}>
            A lone user inside a multi-user account is a failed rollout, and it is invisible on a
            user count.
          </p>
        </>
      )}
    </div>
  );
}

/**
 * How much has the customer built here?
 *
 * The strongest retention signal in the catalog. A tenant with 40 custom fields and 6 workflows has
 * invested weeks of somebody's work; a tenant on defaults has invested nothing.
 */
export function ConfigurationPanel({ configuration }: { configuration: Configuration }) {
  const counts: { label: string; value: number | null }[] = [
    { label: 'Custom modules', value: configuration.custom_modules },
    { label: 'Custom fields', value: configuration.custom_fields },
    { label: 'Layouts', value: configuration.layouts },
    { label: 'Workflows', value: configuration.workflows },
    { label: 'Pipelines', value: configuration.pipelines },
    { label: 'Blueprints', value: configuration.blueprints },
  ];

  // The growth curve is the value here — a single count says almost nothing, while a rising series
  // says the account is being adopted. Only modules with more than one point can draw one.
  const trendable = configuration.modules.filter((m) => m.points.length > 1);

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Configuration</h2>
        {configuration.complexity_total !== null && (
          <span className="t-label subtle">{configuration.complexity_total} things configured</span>
        )}
      </div>

      {/* Two flags, two possible warnings. Either database can be absent while the other reads
          fine, and a combined message would overstate what is missing. */}
      {!configuration.metadata_collected && (
        <div className="callout callout-warning">
          <strong>Metadata not read.</strong> Modules, fields, layouts and record volumes are unknown
          for this tenant rather than zero.
        </div>
      )}

      {!configuration.workflow_collected && (
        <div className="callout callout-warning">
          <strong>Workflow not read.</strong> Workflow and automation figures are unknown rather than
          zero — this account may well have both.
        </div>
      )}

      <div className="kpi-row">
        {counts.map((c) => (
          <StatTile key={c.label} label={c.label} value={c.value} />
        ))}
      </div>

      {configuration.modules.length > 0 && (
        <>
          <h3 className="t-label muted" style={{ margin: '16px 0 8px' }}>Records per module</h3>
          <BarList
            rows={configuration.modules.map((m) => ({
              label: m.module_id,
              value: m.latest ?? 0,
            }))}
            limit={8}
          />
        </>
      )}

      {trendable.length > 0 && (
        <>
          <h3 className="t-label muted" style={{ margin: '16px 0 8px' }}>
            Growth — {trendable[0]!.module_id}
          </h3>
          {/* A line, because the x axis is ordered time. The growth curve is the whole reason the
              volume table appends rather than overwrites. */}
          <TrendChart
            series={[{
              key: trendable[0]!.module_id,
              label: trendable[0]!.module_id,
              // The brand blue — one series, so the first categorical slot is the right choice and
              // the ramp in viz.ts would be illegal here (this is not an ordered category).
              color: 'var(--primary)',
              points: trendable[0]!.points.map((p) => ({
                date: p.collected_at.slice(0, 10),
                value: p.record_count ?? 0,
              })),
            }]}
          />
          <p className="t-caption subtle" style={{ margin: '8px 0 0' }}>
            {trendable[0]!.growth !== null && trendable[0]!.growth !== 0
              ? `${trendable[0]!.growth! > 0 ? '+' : ''}${formatCompact(trendable[0]!.growth!)} records since the first collection.`
              : 'Flat since the first collection.'}
          </p>
        </>
      )}

      {configuration.automation.length > 0 && (
        <>
          <h3 className="t-label muted" style={{ margin: '16px 0 8px' }}>Automation</h3>
          <table className="table">
            <thead>
              <tr>
                <th>Day</th>
                <th className="num">Runs</th>
                <th className="num">Failures</th>
                <th className="num">Failure rate</th>
              </tr>
            </thead>
            <tbody>
              {configuration.automation.slice(-14).reverse().map((a) => (
                <tr key={a.date}>
                  <td className="muted">{a.date}</td>
                  <td className="num">{a.runs ?? '—'}</td>
                  <td className="num" style={{ color: (a.failures ?? 0) > 0 ? 'var(--warning)' : undefined }}>
                    {a.failures ?? '—'}
                  </td>
                  {/* Null on a day nothing ran. 0% there would read as a day that went well. */}
                  <td className="num">
                    {a.failure_rate === null ? '—' : `${Math.round(a.failure_rate * 100)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
